/**
 * Frontend mirror of the A4.3.3 ad-creation matrices in
 * `src/api/models/meta_ad.py` — kept in sync manually (backend is the
 * security boundary, this file gates UI options for UX).
 *
 * If you edit one side, edit the other. Backend will reject mismatches
 * with 422 (Pydantic) or 400 (router-level), so a drift surfaces as a
 * Meta-side or Flouri-side validation error rather than a silent
 * shipping bug. Still: mirror.
 */

import type {
  MetaAdCreateStatus,
  MetaCallToActionType,
} from '@/services/api/metaAds';

/** Call-to-action options surfaced in the LinkCreativeStep dropdown.
 *  Order matters: the first entry is the wizard's default and the first
 *  option in the dropdown. LEARN_MORE is the universally-accepted
 *  default — see the matrix-default lesson (META_INTEGRATION_PLAN.md
 *  §16) carried from PR #181 round 1. */
export const CALL_TO_ACTION_OPTIONS: ReadonlyArray<{
  value: MetaCallToActionType;
  label: string;
}> = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'BOOK_TRAVEL', label: 'Book Now' },
  { value: 'APPLY_NOW', label: 'Apply Now' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'SUBSCRIBE', label: 'Subscribe' },
  { value: 'WATCH_MORE', label: 'Watch More' },
  { value: 'ORDER_NOW', label: 'Order Now' },
  { value: 'NO_BUTTON', label: 'No Button' },
];

/** Soft caps from Meta docs. Backend Pydantic enforces; the UI surfaces
 *  live character counts when the input crosses the threshold. */
export const LINK_CHAR_LIMITS = {
  headline: 40,
  primary_text: 125,
  description: 30,
} as const;

/** Display labels for ad-type tiles in CreativeLibraryPicker. Video +
 *  Carousel land in A4.3.3.1; their tiles render disabled with a
 *  "Coming soon" tooltip in this PR. */
export const AD_TYPE_LABELS: Record<
  'link' | 'video' | 'carousel',
  { title: string; subtitle: string }
> = {
  link: {
    title: 'Image Ad',
    subtitle: 'Single image with headline and CTA',
  },
  video: {
    title: 'Video Ad',
    subtitle: 'Coming in A4.3.3.1',
  },
  carousel: {
    title: 'Carousel Ad',
    subtitle: 'Coming in A4.3.3.1',
  },
};

/** Ad-status radio options. PAUSED is the safer default and ships first. */
export const STATUS_OPTIONS: ReadonlyArray<{
  value: MetaAdCreateStatus;
  label: string;
  description: string;
}> = [
  {
    value: 'PAUSED',
    label: 'Paused',
    description: 'Create the ad but don’t start serving yet.',
  },
  {
    value: 'ACTIVE',
    label: 'Active',
    description:
      'Start serving immediately once Meta finishes ad review.',
  },
];

/** Picker filter chips — keys match `MetaAdCreativeKind` from metaAds.ts.
 *  Order: "All" first so the picker opens unfiltered. */
export const CREATIVE_KIND_FILTERS: ReadonlyArray<{
  value: 'all' | 'image' | 'video' | 'carousel';
  label: string;
}> = [
  { value: 'all', label: 'All' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
  { value: 'carousel', label: 'Carousel' },
];

/** Meta placement keys the link-ad picker can target. Mirrored from
 *  `SPEC_REGISTRY` in `src/api/models/platform_specs.py` (Meta entries).
 *  Shared between LinkCreativeStep (aspect-ratio chooser tiles) and
 *  AdReviewStep (human-readable "Placement: …" line). */
export type MetaAdPlacementKey =
  | 'meta_ads.feed_square'
  | 'meta_ads.feed_landscape'
  | 'meta_ads.story';

export const META_PLACEMENT_OPTIONS: ReadonlyArray<{
  placementKey: MetaAdPlacementKey;
  label: string;
  ratio: string;
  description: string;
}> = [
  {
    placementKey: 'meta_ads.feed_square',
    label: 'Feed (Square)',
    ratio: '1:1',
    description: 'Best for primary Feed delivery.',
  },
  {
    placementKey: 'meta_ads.feed_landscape',
    label: 'Feed (Landscape)',
    ratio: '1.91:1',
    description: 'Widescreen Feed format.',
  },
  {
    placementKey: 'meta_ads.story',
    label: 'Story / Reel',
    ratio: '9:16',
    description: 'Full-screen vertical placements.',
  },
];

/** Lookup from placement key → human label for the "Placement: …" line
 *  on the Review step (and anywhere else that needs to show the user a
 *  readable name instead of the wire key). Built from META_PLACEMENT_OPTIONS
 *  so the two stay in lockstep. */
export const META_PLACEMENT_LABELS: Record<string, string> =
  Object.fromEntries(
    META_PLACEMENT_OPTIONS.map((opt) => [opt.placementKey, opt.label]),
  );
